import React from 'react';
import ReactDOM from 'react-dom';
import { hideOthers } from 'aria-hidden';
import { Box, Flex, Center } from '../Layout';
import { CloseBigIcon } from '../Icons';
import { Text } from '../Typography';
import { noop } from '../../utils';
import { useTheme } from '../../theme';
import { useModalClasses } from './useModalClasses';
import { join } from '../../utils/className';
import { useClickOutside, useFocusTrap, useKeyDown, useMergeRefs } from '../../hooks';
import { RemoveScroll } from 'react-remove-scroll';
import { ESCAPE } from '../../constants/key';
import { isRefObject } from '../../utils/isRefObject';
import { Loader } from '../Loader';
const defaultZIndex = 1000;
/**
 * A `Modal` is a window overlaid on the primary window. Content behind the modal is inert, meaning that users cannot interact with it.
 * It follows the [WAI-ARIA Modal Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/dialogmodal/).
 *
 * @see https://fe-docs.int.getresponse.com/gr-ui-neo/index.html?path=/docs/components-modal--page
 */
export const Modal = ({ children, triggerRef, overlayRef: propsOverlayRef, portalContainerRef: propsPortalContainerRef, isOpen, onClose = noop, isCloseable = true, className, size = 'small', title, description, label, shouldAutofocus = true, closeButtonProps, isLoading, overlayProps, zIndex, ...boxProps }) => {
    const theme = useTheme();
    const modalRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    const assignOverlayRef = useMergeRefs(overlayRef, propsOverlayRef);
    const portalContainerRef = React.useRef(null);
    const assignPortalContainerRef = useMergeRefs(portalContainerRef, propsPortalContainerRef);
    useFocusTrap({ elementRef: modalRef, enabled: isOpen });
    const handleOnClose = React.useCallback(() => {
        var _a;
        if (triggerRef) {
            const ref = isRefObject(triggerRef) ? triggerRef.current : triggerRef;
            (_a = ref === null || ref === void 0 ? void 0 : ref.focus) === null || _a === void 0 ? void 0 : _a.call(ref);
        }
        onClose();
    }, [onClose, triggerRef]);
    useClickOutside({
        elementRef: [modalRef, portalContainerRef],
        onClickOutside: handleOnClose,
        containerRef: overlayRef,
        enabled: isOpen,
    });
    useKeyDown({ key: ESCAPE, onKeyDown: handleOnClose, enabled: isOpen && isCloseable });
    React.useEffect(() => {
        if (!isOpen || !modalRef.current) {
            return;
        }
        hideOthers(modalRef.current);
        if (shouldAutofocus) {
            const defaultFocusableElement = modalRef.current.querySelector('a[href], button:not(#close_modal), textarea, input, select');
            defaultFocusableElement === null || defaultFocusableElement === void 0 ? void 0 : defaultFocusableElement.focus();
        }
    }, [isOpen, modalRef, shouldAutofocus]);
    const classes = useModalClasses({
        size,
        theme,
        isLoading,
    });
    if (!isOpen) {
        return null;
    }
    return ReactDOM.createPortal(React.createElement(RemoveScroll, null,
        React.createElement(Flex, { position: "fixed", overflow: "auto", top: "0px", left: "0px", right: "0px", bottom: "0px", background: "rgba(32, 39, 48, 0.7)", zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : defaultZIndex, width: "100%", height: "100%", ref: assignOverlayRef },
            React.createElement(Box, { height: "auto", width: "100%", "aria-modal": true, role: "dialog", "aria-label": label ? label : title, position: "relative", className: join(className, classes.modal), ...overlayProps },
                React.createElement(Box, { position: "relative", margin: "auto", width: "auto", boxShadow: "dropdown", borderRadius: "base", background: theme.components.modal.background, zIndex: 2000, className: classes['modal-body'], ...boxProps, ref: modalRef },
                    isLoading ? (React.createElement(Center, { position: "absolute", width: "100%", height: "100%", top: 0, left: 0 },
                        React.createElement(Loader, { color: "sky", size: "2rem" }))) : null,
                    React.createElement("div", { className: classes.content },
                        isCloseable ? (React.createElement(CloseBigIcon, { className: classes.icon, label: "Close modal", id: "close_modal", onClick: handleOnClose, ...closeButtonProps })) : null,
                        title ? (React.createElement(Text, { marginBottom: "md", variant: "header-2" }, title)) : null,
                        description ? React.createElement("div", { className: classes.description }, description) : null,
                        children))),
            React.createElement(Box, { position: "absolute", top: 0, left: 0, right: 0, ref: assignPortalContainerRef }))), document.body);
};
